import type { SubscriptionDetails } from "schemas/generated/billing";
import type { Workspace } from "schemas/generated/workspace";

import * as React from "react";

import Coin from "@svg/billing/Coin";
import Globe from "@svg/billing/Globe";

import { Button } from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import useChatMessagesUsage from "@v2/features/chat/hooks/useChatMessagesUsage";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { Rocket } from "lucide-react";
import { AddOn } from "schemas/addOns";

import { PlanChangeConfirmationModal } from "../modals/PlanChangeConfirmationModal";
import {
  getCountOfAdditionalProjectsInSubscription,
  getCountOfAdditionalSitesInSubscription,
} from "../shared/sharedBillingUtils";
import SubscriptonCancelledNotEndedAlert from "../shared/SubscriptonCancelledNotEndedAlert";
import UpsellCard from "../UpsellCard";
import PlanHeader from "./PlanHeader";
import ReploSitesUsageSummaryCard from "./ReploSitesUsageSummaryCard";
import ShopifyThemeBuilderUsageSummaryCard from "./ShopifyThemeBuilderUsageSummaryCard";
import SimpleChatMessagesUsage from "./SimpleChatMessagesUsage";

const isUsageLimitReached = ({
  current,
  maximum,
}: {
  current: number;
  maximum: number | null;
}) => {
  if (maximum === null) {
    return false;
  }
  if (maximum === 0) {
    return current > 0;
  }
  return current >= maximum;
};

const PlanSummary = ({
  activeSubscription,
  workspace,
}: {
  activeSubscription?: SubscriptionDetails;
  workspace: Workspace;
}) => {
  const { data: reploSitesUsage } = workspaceRouter.getReploSitesUsage.useQuery(
    workspace.id,
  );

  const { data: shopifyThemeProjectsUsage } =
    workspaceRouter.getShopifyThemeProjectsUsage.useQuery(workspace.id);

  const chatMessagesUsage = useChatMessagesUsage();

  const { data: publishedItemsUsage } =
    workspaceRouter.getPublishedElementsUsage.useQuery(workspace.id);

  const hasReachedReploSitesUsageLimit =
    reploSitesUsage &&
    isUsageLimitReached({
      current: reploSitesUsage.current,
      maximum: reploSitesUsage.maximum,
    });

  const hasReachedShopifyThemeProjectsUsageLimit =
    shopifyThemeProjectsUsage &&
    isUsageLimitReached({
      current: shopifyThemeProjectsUsage.current,
      maximum: shopifyThemeProjectsUsage.maximum,
    });

  const hasReachedPublishedItemsUsageLimit =
    publishedItemsUsage &&
    isUsageLimitReached({
      current: publishedItemsUsage.current,
      maximum: publishedItemsUsage.maximum,
    });

  const hasReachedChatMessagesUsageLimit =
    chatMessagesUsage &&
    isUsageLimitReached({
      current: chatMessagesUsage.current,
      maximum: chatMessagesUsage.maximum,
    });

  const isShopifyBilled = workspace.paymentProcessor === "shopify";

  const hasCancelledSubscription = Boolean(activeSubscription?.endedAt);

  if (!activeSubscription) {
    const shouldShowChatMessagesUsage = !isShopifyBilled;

    // NOTE (Cole, 2025-12-02): Free users can't have published items, and they can't have paid shops either, so we only need to check for chat messages.
    const hasReachedAtLeastOneUsageLimit =
      hasReachedChatMessagesUsageLimit ||
      hasReachedShopifyThemeProjectsUsageLimit;

    return (
      <div className="flex flex-col gap-6">
        {hasReachedAtLeastOneUsageLimit && <FreeLimitReachedUpsellCard />}
        <div className="flex w-full min-w-200 rounded-2xl shadow-sm">
          <PlanHeader
            billingTier="free"
            activeSubscription={activeSubscription}
            workspaceId={workspace.id}
            className={twMerge(
              "rounded-l-2xl",
              !shouldShowChatMessagesUsage && "rounded-r-2xl",
            )}
          />
          {shouldShowChatMessagesUsage && (
            <SimpleChatMessagesUsage
              currentChatMessages={chatMessagesUsage?.current ?? 0}
              maximumChatMessages={chatMessagesUsage?.maximum ?? null}
              className="border-l-0 rounded-r-2xl"
            />
          )}
        </div>
      </div>
    );
  } else if (isShopifyBilled) {
    // NOTE (Cole, 2025-12-02): Shopify billed users can't have chat messages, so we only need to check for shops and published items.
    const hasReachedAtLeastOneUsageLimit =
      hasReachedShopifyThemeProjectsUsageLimit ||
      hasReachedPublishedItemsUsageLimit;

    return (
      <div className="flex flex-col min-w-140 gap-6 mb-16">
        {hasReachedAtLeastOneUsageLimit &&
          activeSubscription.billingTier === "starter" &&
          activeSubscription &&
          !hasCancelledSubscription && (
            <StarterLimitReachedUpsellCard
              activeSubscription={activeSubscription}
            />
          )}
        <div className="flex flex-col w-full rounded-2xl shadow-sm">
          <PlanHeader
            billingTier={activeSubscription.billingTier}
            activeSubscription={activeSubscription}
            workspaceId={workspace.id}
            className="rounded-2xl"
          />
        </div>
        <SubscriptonCancelledNotEndedAlert
          activeSubscription={activeSubscription}
          workspaceId={workspace.id}
        />
        <ShopifyThemeBuilderUsageSummaryCard
          currentProjects={shopifyThemeProjectsUsage?.current ?? 0}
          maximumProjects={shopifyThemeProjectsUsage?.maximum ?? null}
          currentPublishedItems={publishedItemsUsage?.current ?? 0}
          maximumPublishedItems={publishedItemsUsage?.maximum ?? null}
        />
      </div>
    );
  } else {
    const hasReachedAtLeastOneUsageLimit =
      hasReachedReploSitesUsageLimit ||
      hasReachedShopifyThemeProjectsUsageLimit ||
      hasReachedPublishedItemsUsageLimit ||
      hasReachedChatMessagesUsageLimit;

    return (
      <div className="flex flex-col min-w-140 gap-6 mb-16">
        {hasReachedAtLeastOneUsageLimit &&
          activeSubscription.billingTier === "starter" &&
          activeSubscription &&
          !hasCancelledSubscription && (
            <StarterLimitReachedUpsellCard
              activeSubscription={activeSubscription}
            />
          )}
        <div className="flex flex-col w-full rounded-2xl shadow-sm">
          <PlanHeader
            billingTier={activeSubscription.billingTier}
            activeSubscription={activeSubscription}
            workspaceId={workspace.id}
            className="rounded-2xl"
          />
        </div>
        <SubscriptonCancelledNotEndedAlert
          activeSubscription={activeSubscription}
          workspaceId={workspace.id}
        />
        <ReploSitesUsageSummaryCard
          currentSites={reploSitesUsage?.current ?? 0}
          maximumSites={reploSitesUsage?.maximum ?? null}
          currentChatMessages={chatMessagesUsage?.current ?? 0}
          maximumChatMessages={chatMessagesUsage?.maximum ?? null}
          nextChatResetDate={chatMessagesUsage?.nextResetDate}
        />
        <ShopifyThemeBuilderUsageSummaryCard
          currentProjects={shopifyThemeProjectsUsage?.current ?? 0}
          maximumProjects={shopifyThemeProjectsUsage?.maximum ?? null}
          currentPublishedItems={publishedItemsUsage?.current ?? 0}
          maximumPublishedItems={publishedItemsUsage?.maximum ?? null}
        />
      </div>
    );
  }
};

const FreeLimitReachedUpsellCard = () => {
  const [planChangeModalOpen, setPlanChangeModalOpen] = React.useState(false);

  return (
    <>
      <UpsellCard
        title="You're on a roll"
        description="Unlock more to keep the momentum going. Upgrade to Starter."
        icon={<Coin width={56} height={64} />}
        cta={
          <Button
            variant="primary"
            size="base"
            startEnhancer={<Rocket />}
            onClick={() => {
              setPlanChangeModalOpen(true);
            }}
          >
            Upgrade
          </Button>
        }
      />
      <PlanChangeConfirmationModal
        open={planChangeModalOpen}
        onOpenChange={setPlanChangeModalOpen}
        newTier="starter"
        newBillingInterval="monthly"
        addOns={[]}
      />
    </>
  );
};

const StarterLimitReachedUpsellCard = ({
  activeSubscription,
}: {
  activeSubscription: SubscriptionDetails;
}) => {
  const [planChangeModalOpen, setPlanChangeModalOpen] = React.useState(false);

  const buildAddOns = () => {
    const additionalSites =
      getCountOfAdditionalSitesInSubscription(activeSubscription);
    const additionalProjects =
      getCountOfAdditionalProjectsInSubscription(activeSubscription);
    return AddOn.buildFromCounts({
      additionalSites,
      additionalProjects,
      additionalPublishedElementBundles: 0,
    });
  };

  return (
    <>
      <UpsellCard
        title="Ready to scale?"
        description="Go Pro for unlimited pages and volume discounts."
        icon={<Globe width={72} height={72} />}
        cta={
          <Button
            variant="primary"
            size="base"
            startEnhancer={<Rocket />}
            onClick={() => {
              setPlanChangeModalOpen(true);
            }}
          >
            Upgrade
          </Button>
        }
      />
      <PlanChangeConfirmationModal
        open={planChangeModalOpen}
        onOpenChange={setPlanChangeModalOpen}
        newTier="pro"
        newBillingInterval={activeSubscription.billingInterval}
        addOns={buildAddOns()}
      />
    </>
  );
};

export default PlanSummary;
